import React, { useState, useEffect, useCallback } from "react";
import {
  adminVerify,
  adminListNewsletter,
  adminDeleteNewsletter,
  adminListMessages,
  adminDeleteMessage,
  clearToken,
  getToken,
} from "../lib/api";
import { useToast } from "../hooks/use-toast";
import LoginScreen from "./admin/LoginScreen";
import DashboardHeader from "./admin/DashboardHeader";
import TabStrip from "./admin/TabStrip";
import NewsletterPanel from "./admin/NewsletterPanel";
import MessagesPanel from "./admin/MessagesPanel";

function Dashboard({ onLogout }) {
  const { toast } = useToast();
  const [tab, setTab] = useState("newsletter");
  const [newsletter, setNewsletter] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [n, m] = await Promise.all([adminListNewsletter(), adminListMessages()]);
      setNewsletter(n);
      setMessages(m);
    } catch (err) {
      if (err?.response?.status === 401) {
        clearToken();
        onLogout();
        return;
      }
      toast({ title: "Failed to load data" });
    } finally {
      setLoading(false);
    }
  }, [onLogout, toast]);

  // Initial data load on mount. `loadAll` is memoised with `useCallback`
  // so it is stable between renders unless `onLogout`/`toast` change.
  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleDeleteNewsletter = useCallback(
    async (id) => {
      if (!window.confirm("Remove this subscriber?")) return;
      try {
        await adminDeleteNewsletter(id);
        setNewsletter((arr) => arr.filter((x) => x.id !== id));
        toast({ title: "Subscriber removed" });
      } catch (e) {
        toast({ title: "Could not delete" });
      }
    },
    [toast],
  );

  const handleDeleteMessage = useCallback(
    async (id) => {
      if (!window.confirm("Delete this message?")) return;
      try {
        await adminDeleteMessage(id);
        setMessages((arr) => arr.filter((x) => x.id !== id));
        toast({ title: "Message deleted" });
      } catch (e) {
        toast({ title: "Could not delete" });
      }
    },
    [toast],
  );

  const handleSignOut = useCallback(() => {
    clearToken();
    onLogout();
  }, [onLogout]);

  function renderTabContent() {
    if (loading) return <p className="text-[#7d8e54]">Loading...</p>;
    if (tab === "newsletter") {
      return <NewsletterPanel items={newsletter} onDelete={handleDeleteNewsletter} />;
    }
    return <MessagesPanel items={messages} onDelete={handleDeleteMessage} />;
  }

  return (
    <div className="nn-page-bg min-h-screen">
      <div className="max-w-[1140px] mx-auto bg-white min-h-screen shadow-[0_0_30px_rgba(0,0,0,0.08)]">
        <DashboardHeader onSignOut={handleSignOut} />
        <TabStrip
          tab={tab}
          onChangeTab={setTab}
          newsletterCount={newsletter.length}
          messagesCount={messages.length}
          onRefresh={loadAll}
        />
        <div className="p-6">{renderTabContent()}</div>
      </div>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);

  const checkSession = useCallback(async () => {
    if (!getToken()) {
      setChecking(false);
      return;
    }
    try {
      await adminVerify();
      setAuthed(true);
    } catch (e) {
      clearToken();
      setAuthed(false);
    } finally {
      setChecking(false);
    }
  }, []);

  // Auth bootstrap on mount: verify any persisted token.
  useEffect(() => {
    checkSession();
  }, [checkSession]);

  if (checking) {
    return (
      <div className="nn-page-bg min-h-screen flex items-center justify-center">
        <p className="text-white font-display text-xl">Loading...</p>
      </div>
    );
  }

  if (authed) {
    return <Dashboard onLogout={() => setAuthed(false)} />;
  }
  return <LoginScreen onLogin={() => setAuthed(true)} />;
}
